import { useContext, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styles from './product-type-filter.module.scss';
import { augmentClassName } from '../../../../utils/GlobalCssProxy';
import useSideRailContext from '../../../../stores/profile/use-side-rail-context';
import screenerAppContext from '../../../../stores/screener/screener-app-context';
import Checkbox from '../../../atoms/checkbox';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Form from 'react-bootstrap/Form';
import Icon from '../../../atoms/icon';
import InteractiveElement from '../../../atoms/interactive-element';
import { getModels } from '../../../../utils/sharedHelpers';
import {
  updateProductFilter,
  updateFundTypeFilter
} from '../../../../stores/screener/screener-redux-store';
import {
  PRODUCT_TYPE_MODELS,
  PRODUCT_TYPE_TO_MODEL,
  CHANNELS
} from '../../../../shared/screener-constants';
import ButtonViewGlossary from '../../../atoms/button-view-glossary';

const className = augmentClassName(styles);

export const ProductTypeFilter = ({
  filters,
  productTypesFromPlan,
  updateFundTypeCheckBox,
  updateProductTypeCheckBox,
  isSearchedPlanInContext
}) => {
  const [variableAnnuitiesVisible, setShowVariableAnnuitiesVisible] = useState(true);
  const { channel } = useContext(screenerAppContext);
  const sideRail = useSideRailContext();
  const [currentProductFilters, setCurrentProductFilters] = useState([]);
  const updateState = (modelKey, option = null) => {
    if (channel === CHANNELS.MYTIAA || isSearchedPlanInContext) {
      updateProductTypeCheckBox(modelKey);
    } else {
      updateFundTypeCheckBox(modelKey, option);
    }
  };

  const VAParent = filters[PRODUCT_TYPE_MODELS.VARIABLEANNUITIES];

  useEffect(() => {
    setCurrentProductFilters(getModels(productTypesFromPlan, filters, PRODUCT_TYPE_TO_MODEL));
  }, [productTypesFromPlan, filters]);

  if (
    channel === CHANNELS.MYTIAA ||
    channel === CHANNELS.DYNAMICMICROSITES ||
    isSearchedPlanInContext
  ) {
    return (
      <Col {...className('py-6')}>
        <Row {...className('head-row')}>
          <h3 {...className('title')}> Retirement products</h3>
          <button
            {...className('px-3', 'btn-reset', 'mb-1')}
            // TIAA Analytics Tracking
            data-analytics-linkname="GlossaryExpand"
            data-analytics-section="component"
            data-analytics-componenttype="TextLink"
            // END TIAA Analytics Tracking
            aria-label="Open Glossary: Product Type"
            onClick={() => sideRail.glossary.show()}
          >
            <Icon variant="QuestionMarkCircledSolid" />
          </button>
        </Row>
        <div
          // TIAA Analytics Tracking
          data-analytics-linkname="TIAA_retirement_products"
          data-analytics-section="component"
          data-analytics-componenttype="CustomFilters"
          // END TIAA Analytics Tracking
        >
          {currentProductFilters.map((filterModel, index) => (
            <CheckboxRow
              data-key={`${filterModel.key || ''}-${index}`}
              filter={filterModel}
              isFundType={false}
              key={`${filterModel.key || ''}-${index}`}
              onChange={updateState}
            />
          ))}
        </div>
      </Col>
    );
  }

  return (
    <Col {...className('py-6')}>
      <Row {...className('head-row')}>
        <h3 {...className('title')}> TIAA retirement products </h3>
        <ButtonViewGlossary glossarySection="Product Type" solid isScreener />
      </Row>
      <div
        // TIAA Analytics Tracking
        data-analytics-linkname="TIAA_retirement_products"
        data-analytics-section="component"
        data-analytics-componenttype="CustomFilters"
        // END TIAA Analytics Tracking
      >
        <CheckboxRow filter={filters[PRODUCT_TYPE_MODELS.FIXEDANNUITIES]} onChange={updateState} />
        <span {...className('d-inline-block')}>
          <CheckboxRow
            filter={filters[PRODUCT_TYPE_MODELS.VARIABLEANNUITIES]}
            onChange={updateState}
          />
        </span>
        <InteractiveElement
          as="span"
          {...className('d-inline-block', 'show-var-ann')}
          onClick={() => setShowVariableAnnuitiesVisible(!variableAnnuitiesVisible)}
        >
          {variableAnnuitiesVisible ? (
            <Icon.ChevronUp height="12px" width="14px" />
          ) : (
            <Icon.ChevronDown height="12px" width="14px" />
          )}
        </InteractiveElement>
        {variableAnnuitiesVisible ? (
          <div {...className('sub-filter')}>
            <CheckboxRow
              filter={filters[PRODUCT_TYPE_MODELS.CREF]}
              onChange={updateState}
              parentRow={VAParent}
            />
            <CheckboxRow
              filter={filters[PRODUCT_TYPE_MODELS.TIAAACCESS]}
              onChange={updateState}
              parentRow={VAParent}
            />
            <CheckboxRow
              filter={filters[PRODUCT_TYPE_MODELS.TIAAREALESTATE]}
              onChange={updateState}
              parentRow={VAParent}
            />
          </div>
        ) : (
          <div></div>
        )}
        <CheckboxRow filter={filters[PRODUCT_TYPE_MODELS.MUTUALFUNDS]} onChange={updateState} />
        <CheckboxRow
          filter={filters[PRODUCT_TYPE_MODELS.FDICINSUREDPRODUCTS]}
          onChange={updateState}
        />
      </div>
    </Col>
  );
};

ProductTypeFilter.propTypes = {
  filters: PropTypes.object.isRequired,
  productTypesFromPlan: PropTypes.array,
  updateFundTypeCheckBox: PropTypes.func,
  updateProductTypeCheckBox: PropTypes.func,
  isSearchedPlanInContext: PropTypes.bool
};

const CheckboxRow = ({ filter, onChange, parentRow = null, isFundType = true }) => {
  const optionSelect = () => {
    const option = document.getElementById(`id_${filter.key}`).value;
    onChange(filter.modelKey, option);
  };
  const dropdownAriaLabel =
    filter.key === 'tiaa-access'
      ? `${filter.label} level dropdown`
      : `${filter.label} class dropdown`;
  const disabled = parentRow && !parentRow.checked;
  return (
    <div>
      <Row>
        <Col xs="auto">
          <Checkbox
            label={filter.label}
            ariaLabel={`${filter.label} product checkbox`}
            id={filter.key}
            disabled={disabled}
            onChange={onChange}
            checked={filter.checked}
            modelKey={filter.modelKey}
            checkedColor="#454545"
          />
        </Col>
        <Col xs="auto">
          <Form.Label {...className('check-label')} aria-label={filter.label} htmlFor={filter.key}>
            {filter.label}
          </Form.Label>
        </Col>
        {filter.options && filter.checked && isFundType && (
          <Col md="auto">
            <select
              id={`id_${filter.key}`}
              aria-label={dropdownAriaLabel}
              onChange={() => {
                optionSelect();
              }}
              {...className('option-select')}
              value={filter.currentOption}
              size="sm"
            >
              {filter.options.map((option, index) => {
                return (
                  <option
                    aria-label={option}
                    data-key={`${option || ''}-${index}`}
                    key={`${option || ''}-${index}`}
                    value={option}
                  >
                    {option}
                  </option>
                );
              })}
            </select>
          </Col>
        )}
      </Row>
    </div>
  );
};

CheckboxRow.propTypes = {
  filter: PropTypes.object.isRequired,
  onChange: PropTypes.func,
  parentRow: PropTypes.object,
  isFundType: PropTypes.bool
};

const mapStateToProps = state => {
  return {
    filters: state.filterReducer.filters.productTypeFilters,
    productTypesFromPlan: state.filterReducer.productTypesFromPlan,
    isSearchedPlanInContext: state.filterReducer.inputs.isSearchedPlanInContext
  };
};

const mapDispatchToProps = dispatch => {
  return {
    updateFundTypeCheckBox: (modelKey, option) => dispatch(updateFundTypeFilter(modelKey, option)),
    updateProductTypeCheckBox: modelKey => dispatch(updateProductFilter(modelKey))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductTypeFilter);
